import React, { useMemo } from 'react';
import { Layout, Seo } from '@layout';
import { RecommendProducts } from '@sections/shared/GridView';
import {
  CompletionSection,
  HeroSection,
  ShopRegistrySection,
  ShopSection,
  RegistrySection,
  GiftingSection,
  ZeroCashFundSection,
  FundSection,
  JoyButtonSection,
} from '@sections/baby-registry';
import { CenteredFaqSection } from '@sections/shared/CenteredFaqSection';
import { FaqList } from '@components/FaqList';
import { BlogCarousel } from '@components/BlogCarousel';
import { graphql } from 'gatsby';
import { AbcSection } from '@sections/shared/AbcSection';
import { useTranslation } from 'react-i18next';

export const BABY_REGISTRY = 'baby-registry';

interface BabyRegistryBlogs {
  id: string;
  blogs: {
    link: string;
    title: string;
    excerpt: string;
    mediaLink: string;
  }[];
}

interface BabyBlissRegistryData {
  recommendCatalogHomeSections: {
    sections: {
      paginatedCatalogEntities: {
        edges: RecommendProducts;
      };
    }[];
  };
}

interface BabyRegistryPageData {
  bliss: BabyBlissRegistryData;
  babyRegistryBlogs: BabyRegistryBlogs;
}

export const query = graphql`
  {
    babyRegistryBlogs {
      id
      blogs {
        link
        title
        excerpt
        mediaLink
      }
    }
    bliss {
      recommendCatalogHomeSections(storefront: baby) {
        sections(offset: 1, limit: 1) {
          paginatedCatalogEntities(first: 12) {
            edges {
              node {
                ... on Bliss_ProductCatalogEntityItemGroup {
                  catalogItemGroupData {
                    tileViewItem {
                      id
                      title
                      brand {
                        name
                      }
                      publicUrlPath
                      mediaAssets {
                        ... on Bliss_ProductCatalogPhotoAsset {
                          photo {
                            url
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const BabyRegistry = ({ data }: { data: BabyRegistryPageData }) => {
  // Filter only first valid 8 products out of 12
  const products: RecommendProducts = useMemo(
    () =>
      data.bliss.recommendCatalogHomeSections.sections[0].paginatedCatalogEntities.edges
        .filter((edge) => edge.node.catalogItemGroupData && edge.node.catalogItemGroupData.tileViewItem)
        .slice(0, 8),
    [data]
  );

  const { t } = useTranslation();

  return (
    <Layout
      transparentHeaderButtonVariant="defaultPrimeWeddingWebsites"
      isTransparentHeader={true}
      headerScrollThresholdVh={35}
      page={BABY_REGISTRY}
    >
      <Seo
        title={t('babyRegistry:seo.title')}
        canonicalURL={'https://withjoy.com/baby-registry/'}
        description={t('babyRegistry:seo.content')}
        ogImage={'common/meta_share/registry.jpg'}
      />
      <HeroSection />
      <ShopRegistrySection />
      <ShopSection products={products} />
      <CompletionSection />
      <RegistrySection />
      <GiftingSection />
      <ZeroCashFundSection />
      <FundSection />
      <JoyButtonSection />
      <CenteredFaqSection
        title={t('babyRegistry:faqList.title')}
        titleColor="neutra6"
        backgroundColor="humana1Light"
        sectionId="Baby_Registry_FAQ"
      >
        <FaqList accordion={true} questionNodes={t('faq:questions.babyRegistry')} color="neutra6" isLinkHidden={true} />
      </CenteredFaqSection>
      <BlogCarousel blogs={data.babyRegistryBlogs.blogs} sectionId="BabyRegistry_Blog" />
      <AbcSection
        abcSectionId="BabyRegistry_ABC_CTA"
        page={BABY_REGISTRY}
        customData={t('babyRegistry:abcSectionData')}
        posterOptimization="default"
      />
    </Layout>
  );
};

export default BabyRegistry;
